import { useEffect, useRef } from "react";
import {
  canRetryExecutionLive,
  EXECUTION_LIVE_DISCONNECTED_RECOVERY_POLL_MS,
  executionLiveReconnectDelayMs,
} from "./execution-live-reconnect";

// A single "resilient subscription" primitive shared by every execution live
// WebSocket surface (single-operation live, portfolio feed, user feed, market
// order). It encapsulates the four resilience behaviors those hooks all need:
//
//   1. A bounded fast reconnect budget with backoff.
//   2. A non-terminal `disconnected` state once that budget is spent — a lost
//      socket is never an operation failure.
//   3. A slow REST recovery poll that heals to the true outcome and re-arms the
//      stream (or, for feeds, simply re-opens for a fresh snapshot).
//   4. Immediate re-arm on `online` / `visibilitychange`→visible so recovery is
//      instant when the network or tab comes back rather than waiting out the
//      backoff.
//
// The transport itself (fetching a *fresh* live descriptor and opening the
// socket) and the shape of the state it drives are supplied by the caller via
// `open`/`recover`; this controller only owns the reconnection lifecycle. The
// caller must always fetch a fresh descriptor inside `open` on reconnect — a
// caller-supplied token may only be honored on the very first connect
// (`context.isInitialConnect`), never reused across reconnects.

/** Connection lifecycle status. Operation/feed status is owned by the caller. */
export type ResilientConnectionStatus =
  | "idle"
  | "connecting"
  | "live"
  | "disconnected";

export type ResilientRecoveryOutcome =
  // The operation resolved server-side (caller already applied the terminal
  // state inside `recover`); stop reconnecting.
  | "terminal"
  // Connectivity returned; reset the budget and re-open the live stream.
  | "recovered"
  // Still unknown; keep the slow recovery poll running.
  | "pending";

export interface ResilientSubscriptionHandle {
  readonly close: () => void;
}

export interface ResilientSubscriptionRunContext {
  /** Sequence the transport should resume from. */
  readonly lastSequence: number;
  /**
   * True only for the very first connect of a scope. Callers may honor a
   * caller-supplied token hint on the initial connect; every reconnect must
   * fetch a fresh live descriptor.
   */
  readonly isInitialConnect: boolean;
  /** True while this connect run is the controller's active run. */
  readonly isCurrent: () => boolean;
  /**
   * Register the open transport handle so the controller can close it on
   * reconnect/teardown. Call this synchronously right after subscribing; if the
   * run is already stale the handle is closed immediately.
   */
  readonly bindSubscription: (handle: ResilientSubscriptionHandle) => void;
  /** A live frame arrived; reset the reconnect budget and mark the socket live. */
  readonly noteActivity: () => void;
  /** The operation reached a terminal state; stop reconnecting. */
  readonly settle: () => void;
  /** The transport closed; drive reconnection. */
  readonly handleClose: () => void;
  /** The transport errored; drive reconnection. */
  readonly handleError: (error: Error) => void;
}

export interface ResilientRecoveryContext {
  readonly isCurrent: () => boolean;
}

export interface ResilientSubscriptionConfig {
  /**
   * Open the transport. Must fetch a fresh live descriptor on every call except
   * that a caller-supplied token hint may be used when
   * `context.isInitialConnect` is true. After subscribing, register the handle
   * synchronously via `context.bindSubscription(handle)`. Wire the transport's
   * close/error into `context.handleClose`/`context.handleError`, call
   * `context.noteActivity()` when a snapshot/event confirms liveness, and
   * `context.settle()` when the operation reaches a terminal state.
   */
  readonly open: (context: ResilientSubscriptionRunContext) => Promise<void>;
  /**
   * Slow REST recovery invoked once the reconnect budget is spent. When
   * omitted, exhaustion simply re-opens the transport after `recoveryPollMs`
   * (feeds heal from a fresh snapshot). See {@link ResilientRecoveryOutcome}.
   */
  readonly recover?: (
    context: ResilientRecoveryContext,
  ) => Promise<ResilientRecoveryOutcome>;
  /** Notified on every connection-status transition. */
  readonly onStatus: (
    status: ResilientConnectionStatus,
    error: string | null,
  ) => void;
  /** Reads the caller's current resume sequence at connect time. */
  readonly getLastSequence: () => number;
  readonly recoveryPollMs?: number;
}

/** Injectable seam for the `online`/visibility re-arm signals (browser by default). */
export interface ResilientReconnectSignals {
  readonly subscribe: (onRearm: () => void) => () => void;
}

export const browserReconnectSignals = (): ResilientReconnectSignals => ({
  subscribe: (onRearm) => {
    if (typeof window === "undefined") {
      return () => {};
    }

    const handleOnline = (): void => {
      onRearm();
    };
    const handleVisibility = (): void => {
      if (
        typeof document === "undefined" ||
        document.visibilityState === "visible"
      ) {
        onRearm();
      }
    };

    window.addEventListener("online", handleOnline);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibility);
    }
    window.addEventListener("focus", handleVisibility);

    return () => {
      window.removeEventListener("online", handleOnline);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
      window.removeEventListener("focus", handleVisibility);
    };
  },
});

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export class ResilientLiveSubscription {
  private config: ResilientSubscriptionConfig;
  private readonly signals: ResilientReconnectSignals;
  private readonly recoveryPollMs: number;

  private subscription: ResilientSubscriptionHandle | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private recoveryTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private generation = 0;
  private stopped = true;
  private settled = false;
  private isInitialConnect = true;
  private connectionStatus: ResilientConnectionStatus = "idle";
  private unsubscribeSignals: (() => void) | null = null;

  constructor(
    config: ResilientSubscriptionConfig,
    signals: ResilientReconnectSignals = browserReconnectSignals(),
  ) {
    this.config = config;
    this.signals = signals;
    this.recoveryPollMs =
      config.recoveryPollMs ?? EXECUTION_LIVE_DISCONNECTED_RECOVERY_POLL_MS;
  }

  /** Replace the config (used to keep caller closures fresh across renders). */
  updateConfig(config: ResilientSubscriptionConfig): void {
    this.config = config;
  }

  get status(): ResilientConnectionStatus {
    return this.connectionStatus;
  }

  /** (Re)start a scope: reset the budget, attach re-arm signals, and connect. */
  start(): void {
    this.stopped = false;
    this.settled = false;
    this.reconnectAttempt = 0;
    this.isInitialConnect = true;
    this.clearReconnectTimer();
    this.clearRecoveryTimer();
    this.attachSignals();
    this.connectionStatus = "connecting";
    this.config.onStatus("connecting", null);
    this.connect();
  }

  /** Tear down: cancel timers, close the socket, detach signals. Re-startable. */
  stop(): void {
    this.stopped = true;
    this.generation += 1;
    this.clearReconnectTimer();
    this.clearRecoveryTimer();
    this.closeSubscription();
    this.detachSignals();
    this.connectionStatus = "idle";
  }

  /**
   * Force an immediate reconnect (reset budget). Invoked by the re-arm signals
   * and safe to call at any time — it no-ops when settled, stopped, or already
   * live.
   */
  rearm(): void {
    if (this.stopped || this.settled) {
      return;
    }
    if (this.connectionStatus === "live") {
      return;
    }

    this.clearReconnectTimer();
    this.clearRecoveryTimer();
    this.reconnectAttempt = 0;
    this.connectionStatus = "connecting";
    this.config.onStatus("connecting", null);
    this.connect();
  }

  private attachSignals(): void {
    if (this.unsubscribeSignals) {
      return;
    }
    this.unsubscribeSignals = this.signals.subscribe(() => {
      this.rearm();
    });
  }

  private detachSignals(): void {
    this.unsubscribeSignals?.();
    this.unsubscribeSignals = null;
  }

  private connect(): void {
    const generation = (this.generation += 1);
    const context = this.buildContext(generation);

    // Close any handle left over from a previous run before opening a new one.
    this.closeSubscription();

    // Call `open` synchronously so its non-awaiting prefix (e.g. a token-hinted
    // subscribe on the initial connect) — and its `bindSubscription` call — run
    // in the current task rather than a deferred microtask.
    let opened: Promise<void>;
    try {
      opened = Promise.resolve(this.config.open(context));
    } catch (error) {
      if (this.generation === generation && !this.stopped) {
        this.scheduleReconnect(errorMessage(error));
      }
      return;
    }

    opened.then(
      () => {
        if (this.generation === generation && !this.stopped) {
          this.isInitialConnect = false;
        }
      },
      (error: unknown) => {
        if (this.generation !== generation || this.stopped) {
          return;
        }
        this.scheduleReconnect(errorMessage(error));
      },
    );
  }

  private bindSubscription(
    generation: number,
    handle: ResilientSubscriptionHandle,
  ): void {
    if (this.generation !== generation || this.stopped || this.settled) {
      handle.close();
      return;
    }
    this.subscription = handle;
    this.isInitialConnect = false;
  }

  private buildContext(generation: number): ResilientSubscriptionRunContext {
    const isCurrent = (): boolean =>
      this.generation === generation && !this.stopped && !this.settled;

    return {
      lastSequence: this.config.getLastSequence(),
      isInitialConnect: this.isInitialConnect,
      isCurrent,
      bindSubscription: (handle) => {
        this.bindSubscription(generation, handle);
      },
      noteActivity: () => {
        if (isCurrent()) {
          this.noteActivity();
        }
      },
      settle: () => {
        if (isCurrent()) {
          this.settle();
        }
      },
      handleClose: () => {
        if (isCurrent()) {
          this.scheduleReconnect(null);
        }
      },
      handleError: (error) => {
        if (isCurrent()) {
          this.scheduleReconnect(error.message);
        }
      },
    };
  }

  private noteActivity(): void {
    this.reconnectAttempt = 0;
    this.connectionStatus = "live";
    this.clearRecoveryTimer();
  }

  private settle(): void {
    this.settled = true;
    this.clearReconnectTimer();
    this.clearRecoveryTimer();
    this.closeSubscription();
  }

  private scheduleReconnect(error: string | null): void {
    if (this.stopped || this.settled || this.reconnectTimer) {
      return;
    }

    const attempt = this.reconnectAttempt;
    if (!canRetryExecutionLive(attempt)) {
      this.enterDisconnected();
      return;
    }

    this.reconnectAttempt = attempt + 1;
    this.connectionStatus = "connecting";
    this.config.onStatus("connecting", error);
    this.closeSubscription();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.stopped || this.settled) {
        return;
      }
      this.connect();
    }, executionLiveReconnectDelayMs(attempt));
  }

  private enterDisconnected(): void {
    this.connectionStatus = "disconnected";
    this.config.onStatus("disconnected", null);
    this.scheduleRecovery();
  }

  private scheduleRecovery(): void {
    if (this.stopped || this.settled || this.recoveryTimer) {
      return;
    }

    this.recoveryTimer = setTimeout(() => {
      this.recoveryTimer = null;
      if (this.stopped || this.settled) {
        return;
      }
      void this.runRecovery();
    }, this.recoveryPollMs);
  }

  private async runRecovery(): Promise<void> {
    const recover = this.config.recover;
    if (!recover) {
      // No REST recovery: re-open the transport. A fresh snapshot heals state.
      this.reopenAfterRecovery();
      return;
    }

    const generation = this.generation;
    const isCurrent = (): boolean =>
      this.generation === generation && !this.stopped && !this.settled;

    let outcome: ResilientRecoveryOutcome;
    try {
      outcome = await recover({ isCurrent });
    } catch {
      outcome = "pending";
    }

    if (!isCurrent()) {
      return;
    }

    if (outcome === "terminal") {
      this.settle();
      return;
    }
    if (outcome === "recovered") {
      this.reopenAfterRecovery();
      return;
    }
    this.scheduleRecovery();
  }

  private reopenAfterRecovery(): void {
    this.reconnectAttempt = 0;
    this.connectionStatus = "connecting";
    this.config.onStatus("connecting", null);
    this.connect();
  }

  private closeSubscription(): void {
    this.subscription?.close();
    this.subscription = null;
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearRecoveryTimer(): void {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = null;
    }
  }
}

export interface UseResilientLiveSubscriptionParams
  extends ResilientSubscriptionConfig {
  /** The subscription runs only while enabled and a scope key is present. */
  readonly enabled: boolean;
  /**
   * Identifies the current scope (portfolio id, user scope, operation id …).
   * A change tears down the old subscription and starts a fresh one; callers
   * must reset their own resume sequence/state for the new scope first.
   */
  readonly scopeKey: string | null;
  /** Injectable re-arm signals (defaults to browser online/visibility). */
  readonly signals?: ResilientReconnectSignals;
}

/**
 * Thin React wrapper around {@link ResilientLiveSubscription} for effect-keyed
 * hooks. The caller owns its own React state and reacts to connection
 * transitions through `onStatus`; this hook only drives the controller
 * lifecycle (start on enable/scope, stop on cleanup) and keeps the caller's
 * closures fresh without re-arming the socket on every render.
 */
export function useResilientLiveSubscription(
  params: UseResilientLiveSubscriptionParams,
): void {
  const { enabled, scopeKey, signals, ...config } = params;
  const controllerRef = useRef<ResilientLiveSubscription | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new ResilientLiveSubscription(config, signals);
  } else {
    controllerRef.current.updateConfig(config);
  }

  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }
    if (!enabled || scopeKey === null) {
      controller.stop();
      return;
    }

    controller.start();
    return () => {
      controller.stop();
    };
    // `config`/`signals` are read live through `updateConfig`; only enable/scope
    // changes should tear down and re-open the socket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scopeKey]);
}
